import React, { useCallback, useEffect, useRef, useState } from 'react';
import Layout from '../../../components/Layout';
import ApiInspector from '../../../components/ApiInspector';
import BopRowActions, { RowIcon } from '../../../components/BopRowActions';
import { showToast } from '../../../components/Toast';
import { usePermesso } from '../../../context/PermessiContext';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { useDirtyForm } from '../../../hooks/useDirtyForm';
import { apiFetch } from '../../../lib/api';
import { oggiRoma } from '../../../utils/dateRoma';
import {
  Badge, BadgeSconosciuto, DipendenteOpzione, MOD_BADGE,
  formatDataOra, formatGiorno, messaggioErrore,
} from './tipi';
import { API_DOCS } from './BadgeRicaricaPage.api';

type FiltroStato = '' | 'attivi' | 'non_attivi';

interface FormBadge {
  id: number | null;
  dipendenteId: string;
  codice: string;
  descrizione: string;
  validoDal: string;
  validoAl: string;
  attivo: boolean;
}

interface CorpoModifica {
  dipendenteId: number;
  descrizione: string | null;
  validoDal: string;
  validoAl: string | null;
  attivo: boolean;
}

const CODICE_VALIDO = /^[A-Z0-9]{4,20}$/;

function formVuoto(codice: string): FormBadge {
  return { id: null, dipendenteId: '', codice, descrizione: '', validoDal: oggiRoma(), validoAl: '', attivo: true };
}

function formDaBadge(b: Badge): FormBadge {
  return {
    id: b.id, dipendenteId: String(b.dipendente.id), codice: b.codice, descrizione: b.descrizione ?? '',
    validoDal: b.validoDal, validoAl: b.validoAl ?? '', attivo: b.attivo,
  };
}

// Stato mostrato in tabella, ricavato dalle date (ora italiana) come fa il backend per il filtro.
function statoBadge(b: Badge, oggi: string): { label: string; badge: string } {
  if (!b.attivo) return { label: 'Disattivato', badge: 'bop-badge--neutral' };
  if (b.validoAl && b.validoAl < oggi) return { label: 'Scaduto', badge: 'bop-badge--warning' };
  if (b.validoDal > oggi) return { label: 'Non ancora valido', badge: 'bop-badge--info' };
  return { label: 'Attivo', badge: 'bop-badge--success' };
}

// Modifica di un badge esistente: usata dalla modale e dall'azione "Disattiva" della riga.
async function inviaModificaBadge(id: number, corpo: CorpoModifica): Promise<void> {
  // @api PUT /api/ricarica/badge/:id {dipendenteId,descrizione,validoDal,validoAl,attivo} -> {success:true}
  await apiFetch(`/api/ricarica/badge/${id}`, { method: 'PUT', body: JSON.stringify(corpo) });
}

// ── Modale di creazione e modifica ───────────────────────────────────────────

interface ModaleBadgeProps {
  iniziale: FormBadge;
  societaId: number;
  dipendenti: DipendenteOpzione[];
  // Titolare attuale del badge in modifica: può non essere più tra i dipendenti ATTIVO
  titolareAttuale: DipendenteOpzione | null;
  onChiudi: () => void;
  onSalvato: () => void;
}

function ModaleBadge({ iniziale, societaId, dipendenti, titolareAttuale, onChiudi, onSalvato }: ModaleBadgeProps) {
  const [form, setForm] = useState<FormBadge>(iniziale);
  const [initialSnapshot, setInitialSnapshot] = useState(() => JSON.stringify(iniziale));
  const [errore, setErrore] = useState('');
  const [salvataggio, setSalvataggio] = useState(false);

  const isDirty = JSON.stringify(form) !== initialSnapshot;
  useDirtyForm(isDirty, iniziale.id === null ? 'Nuovo badge di ricarica' : `Modifica badge ${iniziale.codice}`);

  const opzioni = titolareAttuale && !dipendenti.some((d) => d.id === titolareAttuale.id)
    ? [titolareAttuale, ...dipendenti]
    : dipendenti;

  const chiudiDaSfondo = () => {
    // Con dati non salvati un clic fuori dalla modale non deve farli perdere
    if (!isDirty && !salvataggio) onChiudi();
  };

  const salva = async () => {
    const codice = form.codice.trim().toUpperCase();
    if (!form.dipendenteId) { setErrore('Scegli il dipendente'); return; }
    if (form.id === null && !CODICE_VALIDO.test(codice)) {
      setErrore('Codice badge: da 4 a 20 caratteri, solo lettere maiuscole e cifre');
      return;
    }
    if (!form.validoDal) { setErrore('Indica da quando vale il badge'); return; }
    if (form.validoAl && form.validoAl < form.validoDal) {
      setErrore("La fine della validità è precedente all'inizio");
      return;
    }
    const descrizione = form.descrizione.trim() || null;
    const validoAl = form.validoAl || null;

    setSalvataggio(true);
    setErrore('');
    try {
      if (form.id === null) {
        const corpo = {
          societaId, dipendenteId: Number(form.dipendenteId), codice, descrizione,
          validoDal: form.validoDal, validoAl,
        };
        // @api POST /api/ricarica/badge {societaId,dipendenteId,codice,descrizione,validoDal,validoAl} -> {success:true,data:{id}}
        await apiFetch<{ success: boolean; data: { id: number } }>('/api/ricarica/badge', {
          method: 'POST', body: JSON.stringify(corpo),
        });
        showToast(`Badge ${codice} registrato`, 'success');
      } else {
        await inviaModificaBadge(form.id, {
          dipendenteId: Number(form.dipendenteId), descrizione, validoDal: form.validoDal, validoAl, attivo: form.attivo,
        });
        showToast('Badge aggiornato', 'success');
      }
      setInitialSnapshot(JSON.stringify(form));
      onSalvato();
    } catch (err) {
      setErrore(messaggioErrore(err, 'Errore nel salvataggio del badge'));
    } finally {
      setSalvataggio(false);
    }
  };

  return (
    <div className="bop-modal-overlay" onClick={chiudiDaSfondo}>
      <div className="bop-modal" onClick={(e) => e.stopPropagation()}>
        <div className="bop-modal__header">
          <h2 className="bop-modal__title">{form.id === null ? 'Nuovo badge' : 'Modifica badge'}</h2>
          <button className="bop-modal__close" onClick={onChiudi} disabled={salvataggio} aria-label="Chiudi">✕</button>
        </div>
        <div className="bop-modal__body bop-ricarica__form">
          {errore && <div className="bop-alert bop-alert--danger bop-mb-0">{errore}</div>}
          <div className="bop-field">
            <label className="bop-field__label" htmlFor="badge-dip">
              Dipendente <span className="bop-field__required">*</span>
            </label>
            <select id="badge-dip" className="bop-field__select" value={form.dipendenteId}
              onChange={(e) => setForm({ ...form, dipendenteId: e.target.value })}>
              <option value="">Scegli…</option>
              {opzioni.map((d) => (<option key={d.id} value={d.id}>{d.cognome} {d.nome}</option>))}
            </select>
            {form.id !== null && (
              <p className="bop-field__hint">
                Cambiando dipendente, le ricariche già fatte restano intestate a chi aveva il badge in quel momento.
              </p>
            )}
          </div>
          <div className="bop-field">
            <label className="bop-field__label" htmlFor="badge-codice">
              Codice badge <span className="bop-field__required">*</span>
            </label>
            <input id="badge-codice" className="bop-field__input bop-text-mono" placeholder="Es. 04A1B2C3D4"
              value={form.codice} disabled={form.id !== null} maxLength={20}
              onChange={(e) => setForm({ ...form, codice: e.target.value.toUpperCase() })} />
            <p className="bop-field__hint">
              Il codice che la colonnina legge dal badge. Il modo più semplice per averlo: avvicinare il badge a una
              colonnina e abilitarlo dai badge sconosciuti.
            </p>
          </div>
          <div className="bop-field">
            <label className="bop-field__label" htmlFor="badge-descr">Descrizione</label>
            <input id="badge-descr" className="bop-field__input" placeholder="Es. Badge presenze" maxLength={200}
              value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} />
          </div>
          <div className="bop-form-row">
            <div className="bop-field">
              <label className="bop-field__label" htmlFor="badge-dal">
                Valido dal <span className="bop-field__required">*</span>
              </label>
              <input id="badge-dal" type="date" className="bop-field__input" value={form.validoDal}
                onChange={(e) => setForm({ ...form, validoDal: e.target.value })} />
            </div>
            <div className="bop-field">
              <label className="bop-field__label" htmlFor="badge-al">Valido fino al</label>
              <input id="badge-al" type="date" className="bop-field__input" value={form.validoAl}
                onChange={(e) => setForm({ ...form, validoAl: e.target.value })} />
              <p className="bop-field__hint">Vuoto: senza scadenza</p>
            </div>
          </div>
          {form.id !== null && (
            <label className="bop-flex">
              <input type="checkbox" checked={form.attivo}
                onChange={(e) => setForm({ ...form, attivo: e.target.checked })} />
              Badge attivo
            </label>
          )}
        </div>
        <div className="bop-modal__footer">
          <button className="bop-btn bop-btn-secondary" onClick={onChiudi} disabled={salvataggio}>Annulla</button>
          <button className="bop-btn bop-btn-primary" onClick={salva} disabled={salvataggio}>
            {salvataggio ? 'Salvataggio…' : form.id === null ? 'Registra badge' : 'Salva'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Pagina ───────────────────────────────────────────────────────────────────

export default function BadgeRicaricaPage() {
  const { can } = usePermesso();
  const canEdit = can(MOD_BADGE, 'edit');
  const { societaId, pronta } = useFiltroSocieta();

  const [badge, setBadge] = useState<Badge[]>([]);
  const [caricamento, setCaricamento] = useState(true);
  const [sconosciuti, setSconosciuti] = useState<BadgeSconosciuto[]>([]);
  const [dipendenti, setDipendenti] = useState<DipendenteOpzione[]>([]);

  const [fStato, setFStato] = useState<FiltroStato>('');
  const [fQ, setFQ] = useState('');
  const [qApplicata, setQApplicata] = useState('');

  const [form, setForm] = useState<FormBadge | null>(null);

  const richiestaLista = useRef(0);
  const oggi = oggiRoma();

  // Cambio società: i dati e la modale aperta appartengono a quella di prima
  useEffect(() => {
    setBadge([]);
    setSconosciuti([]);
    setDipendenti([]);
    setForm(null);
  }, [societaId]);

  // La ricerca parte poco dopo l'ultimo carattere digitato
  useEffect(() => {
    const t = setTimeout(() => setQApplicata(fQ.trim()), 300);
    return () => clearTimeout(t);
  }, [fQ]);

  const caricaBadge = useCallback(async () => {
    if (!pronta || !societaId) return;
    const numero = ++richiestaLista.current;
    setCaricamento(true);
    try {
      const p = new URLSearchParams({ societaId });
      if (fStato) p.set('stato', fStato);
      if (qApplicata) p.set('q', qApplicata);
      // @api GET /api/ricarica/badge?societaId&q&stato -> {success:true,data:[{id,codice,descrizione,dipendente:{id,nome,cognome},validoDal,validoAl,attivo,ultimoUtilizzoAt}]}
      const res = await apiFetch<{ success: boolean; data: Badge[] }>(`/api/ricarica/badge?${p}`);
      if (numero === richiestaLista.current) setBadge(res.data);
    } catch (err) {
      if (numero === richiestaLista.current) showToast(messaggioErrore(err, 'Errore nel caricamento dei badge'), 'error');
    } finally {
      if (numero === richiestaLista.current) setCaricamento(false);
    }
  }, [pronta, societaId, fStato, qApplicata]);

  const caricaSconosciuti = useCallback(async () => {
    if (!pronta || !societaId) return;
    try {
      // @api GET /api/ricarica/badge/sconosciuti -> {success:true,data:[{codiceBadge,tentativi,ultimoTentativoAt,colonnina:{id,nome,sede,societaNome}}]}
      const res = await apiFetch<{ success: boolean; data: BadgeSconosciuto[] }>('/api/ricarica/badge/sconosciuti');
      setSconosciuti(res.data);
    } catch (err) {
      showToast(messaggioErrore(err, 'Errore nel caricamento dei badge sconosciuti'), 'error');
    }
  }, [pronta, societaId]);

  const caricaDipendenti = useCallback(async () => {
    if (!pronta || !societaId) return;
    try {
      // @api GET /api/ricarica/dipendenti?societaId -> {success:true,data:[{id,nome,cognome}]}
      const res = await apiFetch<{ success: boolean; data: DipendenteOpzione[] }>(
        `/api/ricarica/dipendenti?societaId=${encodeURIComponent(societaId)}`,
      );
      setDipendenti(res.data);
    } catch (err) {
      showToast(messaggioErrore(err, 'Errore nel caricamento dei dipendenti'), 'error');
    }
  }, [pronta, societaId]);

  useEffect(() => { caricaBadge(); }, [caricaBadge]);
  useEffect(() => { caricaSconosciuti(); }, [caricaSconosciuti]);
  useEffect(() => { caricaDipendenti(); }, [caricaDipendenti]);

  const apriNuovo = (codice = '') => setForm(formVuoto(codice));
  const apriModifica = (b: Badge) => setForm(formDaBadge(b));

  const dopoSalvataggio = () => {
    setForm(null);
    caricaBadge();
    caricaSconosciuti();
  };

  const disattiva = async (b: Badge) => {
    try {
      await inviaModificaBadge(b.id, {
        dipendenteId: b.dipendente.id, descrizione: b.descrizione, validoDal: b.validoDal,
        validoAl: b.validoAl, attivo: false,
      });
      showToast(`Badge ${b.codice} disattivato`, 'success');
      caricaBadge();
    } catch (err) {
      showToast(messaggioErrore(err, 'Errore nella disattivazione del badge'), 'error');
    }
  };

  const titolareAttuale = form && form.id !== null
    ? badge.find((b) => b.id === form.id)?.dipendente ?? null
    : null;

  return (
    <Layout>
      <div className="bop-ricarica__stack">
        <div className="bop-page-header">
          <div className="bop-page-header__text">
            <h2 className="bop-page-title">Badge</h2>
            <p className="bop-page-subtitle">Badge di ricarica dei dipendenti: valgono su tutte le colonnine del gruppo</p>
          </div>
          {canEdit && (
            <button className="bop-btn bop-btn-primary" onClick={() => apriNuovo()} disabled={!societaId}>
              + Nuovo badge
            </button>
          )}
        </div>

        {!societaId ? (
          <div className="bop-card">
            <div className="bop-empty-state">
              <p className="bop-empty-state__text">Seleziona una società in alto per vedere e gestire i badge</p>
            </div>
          </div>
        ) : (
          <>
            {sconosciuti.length > 0 && (
              <div className="bop-card">
                <div className="bop-card__header"><h3 className="bop-card__title">Badge sconosciuti</h3></div>
                <div className="bop-card__body">
                  <p className="bop-ricarica__nota">
                    Tentativi rifiutati negli ultimi 30 giorni sulle colonnine del gruppo. Per registrare un badge nuovo
                    avvicinalo a una colonnina, poi clicca Abilita.
                  </p>
                </div>
                <table className="bop-table">
                  <thead>
                    <tr>
                      <th>Codice</th>
                      <th>Colonnina</th>
                      <th className="bop-text-right">Tentativi</th>
                      <th>Ultimo tentativo</th>
                      {canEdit && <th className="bop-table__th--actions">Azioni</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {sconosciuti.map((s) => (
                      <tr key={s.codiceBadge}>
                        <td className="bop-text-mono">{s.codiceBadge}</td>
                        <td>
                          {s.colonnina.nome}
                          <div className="bop-ricarica__secondaria">
                            {s.colonnina.societaNome}{s.colonnina.sede ? ` · ${s.colonnina.sede}` : ''}
                          </div>
                        </td>
                        <td className="bop-text-right">{s.tentativi}</td>
                        <td>{formatDataOra(s.ultimoTentativoAt)}</td>
                        {canEdit && (
                          <td className="bop-table__td--actions">
                            <button className="bop-btn bop-btn-secondary bop-btn-sm" onClick={() => apriNuovo(s.codiceBadge)}>
                              Abilita
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="bop-filter-bar">
              <select className="bop-select" value={fStato} onChange={(e) => setFStato(e.target.value as FiltroStato)}>
                <option value="">Tutti i badge</option>
                <option value="attivi">Solo attivi</option>
                <option value="non_attivi">Non attivi</option>
              </select>
              <input className="bop-input" placeholder="Cerca codice, dipendente o descrizione…" value={fQ}
                onChange={(e) => setFQ(e.target.value)} />
            </div>

            <div className="bop-card">
              <div className="bop-table-wrapper">
                {caricamento && badge.length === 0 ? (
                  <div className="inline-loading">Caricamento…</div>
                ) : badge.length === 0 ? (
                  <div className="bop-empty-state">
                    <h3 className="bop-empty-state__title">Nessun badge</h3>
                    <p className="bop-empty-state__text">Nessun badge trovato con i filtri selezionati</p>
                  </div>
                ) : (
                  <table className={`bop-table${canEdit ? ' bop-table--clickable' : ''}`}>
                    <thead>
                      <tr>
                        <th>Codice</th>
                        <th>Dipendente</th>
                        <th>Descrizione</th>
                        <th>Validità</th>
                        <th>Ultimo utilizzo</th>
                        <th>Stato</th>
                        {canEdit && <th className="bop-table__th--actions">Azioni</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {badge.map((b) => {
                        const s = statoBadge(b, oggi);
                        return (
                          <tr key={b.id} onClick={canEdit ? () => apriModifica(b) : undefined}>
                            <td className="bop-text-mono">{b.codice}</td>
                            <td>{b.dipendente.cognome} {b.dipendente.nome}</td>
                            <td className="bop-text-muted">{b.descrizione ?? '—'}</td>
                            <td>dal {formatGiorno(b.validoDal)}{b.validoAl ? ` al ${formatGiorno(b.validoAl)}` : ''}</td>
                            <td className="bop-text-muted">{formatDataOra(b.ultimoUtilizzoAt)}</td>
                            <td><span className={`bop-badge ${s.badge}`}>{s.label}</span></td>
                            {canEdit && (
                              <td className="bop-table__td--actions">
                                <BopRowActions actions={[
                                  {
                                    icon: <RowIcon.Disable />, label: 'Disattiva', variant: 'danger',
                                    hidden: !b.attivo, onClick: () => disattiva(b),
                                  },
                                ]} />
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </>
        )}

        {form && societaId && (
          <ModaleBadge
            key={form.id ?? `nuovo-${form.codice}`}
            iniziale={form}
            societaId={parseInt(societaId, 10)}
            dipendenti={dipendenti}
            titolareAttuale={titolareAttuale}
            onChiudi={() => setForm(null)}
            onSalvato={dopoSalvataggio}
          />
        )}
      </div>
      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
